import { Checkbox, TextInput, Tooltip, Typography } from '@neo4j-ndl/react';
import { InformationCircleIconOutline } from '@neo4j-ndl/react/icons';
import type { Address } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import { useDarkMode } from '@nx/ui';
import { CardElement } from '@stripe/react-stripe-js';
import type { StripeCardElement } from '@stripe/stripe-js';
import { useEffect, useMemo, useRef } from 'react';

import type { BillingFormData } from '../../project/billing/shared/billing-form-data';
import { defaultStripeAddress } from '../../project/billing/shared/helpers';
import StripeAddressField from '../../project/billing/shared/stripe-address-field';
import StripeProvider from '../../project/billing/shared/stripe-provider';
import { useStripeSubmit } from '../../project/billing/shared/use-stripe-submit';
import { BaseInstanceCreateSection } from './base-section';

interface StripeFormProps {
  data: BillingFormData;
  onDataChange: (data: Partial<BillingFormData>) => void;
  registerSubmit: (func: (data: BillingFormData) => Promise<void>) => void;
  isServiceAddress: boolean;
  onIsServiceAddressChange: (value: boolean) => void;
}

const Form = ({ data, onDataChange, registerSubmit, isServiceAddress, onIsServiceAddressChange }: StripeFormProps) => {
  // TODO: This component needs to be wrapped by the StripeProvider
  // which we don't want to render every time. So we need to find a way to
  // call submitStripe from within this component during create.
  const submitStripe = useStripeSubmit();

  const darkTheme = useDarkMode();

  const handleAddressChange = (address: Partial<Address>) => {
    onDataChange({
      address: {
        ...data.address,
        ...address,
      },
    });
  };

  const handleServiceAddressChange = (serviceAddress: Partial<Address>) => {
    onDataChange({
      serviceAddress: {
        ...(isNotNullish(data.serviceAddress) ? data.serviceAddress : defaultStripeAddress()),
        ...serviceAddress,
      },
    });
  };

  const handleNameChange = (name: string) => {
    onDataChange({ name });
  };

  const handleCompanyNameChange = (companyName: string) => {
    onDataChange({ companyName });
  };

  const handleIsServiceAddressChange = (checked: boolean) => {
    onIsServiceAddressChange(checked);
    if (checked) {
      onDataChange({ serviceAddress: undefined });
    } else {
      onDataChange({ serviceAddress: { ...defaultStripeAddress(), country: data.address.country } });
    }
  };
  const elementsRef = useRef<StripeCardElement>(null);

  const createOptions = useMemo(
    () => ({
      style: {
        base: {
          color: darkTheme ? '#F5F7FA' : '#151E29',
          lineHeight: '17px',
          fontFamily: "Helvetica, 'Helvetica Neue', Arial, sans-serif",
          fontSmoothing: 'antialiased',
          fontSize: '14px',
          '::placeholder': {
            color: '#aab7c4',
          },
        },
        invalid: {
          color: darkTheme ? '#FFB8C4' : '#CC254B',
          iconColor: darkTheme ? '#FFB8C4' : '#CC254B',
        },
      },
    }),
    [darkTheme],
  );

  useEffect(() => {
    registerSubmit(submitStripe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="flex flex-col gap-4">
      <BaseInstanceCreateSection title="Billing address">
        <div className="@sm:max-w-[50%] flex max-w-full flex-col gap-4 ">
          <TextInput
            value={data.name}
            onChange={(event) => handleNameChange(event.target.value)}
            isFluid
            label="Name"
            isRequired
          />
          <StripeAddressField data={data.address} onChange={handleAddressChange} />
        </div>
      </BaseInstanceCreateSection>
      <BaseInstanceCreateSection
        title={
          <div className="flex items-center gap-2">
            <Typography variant="h6">Service address</Typography>
            <Tooltip type="simple" placement="right" isPortaled={false}>
              <Tooltip.Trigger hasButtonWrapper>
                <InformationCircleIconOutline className="size-5" aria-label="Service address information" />
              </Tooltip.Trigger>
              <Tooltip.Content className="max-w-xs">
                <Typography variant="body-medium">
                  This is the physical address of the company purchasing Neo4j Aura. It is used to calculate any
                  applicable sales taxes.
                </Typography>
              </Tooltip.Content>
            </Tooltip>
          </div>
        }
      >
        <div className="@sm:max-w-[50%] flex max-w-full flex-col gap-4">
          <Checkbox
            isChecked={isServiceAddress}
            onChange={(event) => handleIsServiceAddressChange(event.target.checked)}
            label="Same as billing address"
          />
          {!isServiceAddress && isNotNullish(data.serviceAddress) && (
            <StripeAddressField data={data.serviceAddress} onChange={handleServiceAddressChange} />
          )}
          <TextInput
            isFluid
            value={data.companyName}
            onChange={(event) => handleCompanyNameChange(event.target.value)}
            helpText="If specified, this company name will appear on your invoices."
            label="Company name"
          />
        </div>
      </BaseInstanceCreateSection>
      <BaseInstanceCreateSection title="Payment method">
        <div className="@sm:max-w-[50%] flex max-w-full flex-col gap-4">
          <label className="w-full">
            <span
              className="text-neutral-text-weak mb-2 inline-block text-sm font-normal"
              style={{ letterSpacing: '0.25px', lineHeight: '20px' }}
            >
              Credit/Debit card details
            </span>
            <CardElement
              data-testid="stripe-credit-card"
              onReady={(ref) => (elementsRef.current = ref)}
              options={{ hidePostalCode: true, iconStyle: 'default', ...createOptions }}
            />
          </label>
        </div>
      </BaseInstanceCreateSection>
    </div>
  );
};

export const BillingSection = (props: StripeFormProps) => {
  return (
    <StripeProvider>
      <Form {...props} />
    </StripeProvider>
  );
};
